import React, { useMemo, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const SESSION_COLORS = {
    'Elevator Pitch': '#0784a7',
    'Casual Encounter': '#f59e0b',
    'Sales Presentation': '#10b981',
    'Objection Handling': '#e74c3c',
    'Document Defense': '#8e44ad',
    'Messaging Defense': '#3498db',
    'Investor Pitch': '#e67e22',
};

const MEMBER_COLORS = [
    '#0784a7', '#f59e0b', '#10b981', '#e74c3c', '#8e44ad',
    '#3498db', '#e67e22', '#1abc9c', '#c0392b', '#9b59b6',
    '#2ecc71', '#d35400', '#16a085', '#8b0000', '#6c5ce7',
];

const normalizeType = (type) => {
    if (type === 'Sales Presentation / Demo') return 'Sales Presentation';
    return type;
};

const TeamStackedBarChart = ({ teamMembers, assessmentsStore, companyId, weeks = 12, investorPitchEnabled = false }) => {
    const [groupBy, setGroupBy] = useState('sessionType');
    const activeSessionColors = Object.fromEntries(
        Object.entries(SESSION_COLORS).filter(([key]) => key !== 'Investor Pitch' || investorPitchEnabled)
    );

    const data = useMemo(() => {
        const now = new Date();
        const day = now.getUTCDay();
        const currentWeekStart = new Date(now);
        currentWeekStart.setUTCDate(now.getUTCDate() - day + (day === 0 ? -6 : 1));
        currentWeekStart.setUTCHours(0, 0, 0, 0);

        const weekStarts = [];
        for (let i = weeks - 1; i >= 0; i--) {
            const ws = new Date(currentWeekStart);
            ws.setUTCDate(ws.getUTCDate() - i * 7);
            weekStarts.push(ws);
        }

        // Gather all assessments for team
        const allAssessments = [];
        teamMembers.forEach(member => {
            try {
                const memberAssessments = assessmentsStore.closedAssessmentsForUser(member.id, companyId) || [];
                memberAssessments.forEach(a => {
                    if (a.completedDateTime) {
                        allAssessments.push({ ...a, memberId: member.id, memberName: member.name });
                    }
                });
            } catch (e) { console.error('TeamStackedBarChart: error loading assessments for member', member.id, e); }
        });

        return weekStarts.map(ws => {
            const weekEnd = new Date(ws);
            weekEnd.setUTCDate(weekEnd.getUTCDate() + 7);
            const month = ws.toLocaleDateString('en-US', { month: 'short', timeZone: 'UTC' });
            const day = ws.getUTCDate();

            const weekAssessments = allAssessments.filter(a => {
                const d = new Date(a.completedDateTime);
                return d >= ws && d < weekEnd;
            });

            const entry = { week: `${month} ${day}`, total: weekAssessments.length };

            if (groupBy === 'sessionType') {
                Object.keys(activeSessionColors).forEach(type => {
                    entry[type] = weekAssessments.filter(a => normalizeType(a.sessionType) === type).length;
                });
            } else {
                teamMembers.forEach(m => {
                    entry[m.name] = weekAssessments.filter(a => a.memberId === m.id).length;
                });
            }

            return entry;
        });
    }, [teamMembers, assessmentsStore, weeks, groupBy]);

    const hasData = data.some(w => w.total > 0);

    if (!hasData) {
        return (
            <div style={{ padding: '30px', textAlign: 'center', color: '#999', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #e9ecef' }}>
                <div style={{ fontSize: '14px', fontFamily: 'Montserrat' }}>
                    No team practice data yet.
                </div>
            </div>
        );
    }

    const bars = groupBy === 'sessionType'
        ? Object.keys(activeSessionColors).map(key => (
            <Bar key={key} dataKey={key} stackId="a" fill={activeSessionColors[key]} />
        ))
        : teamMembers.map((m, i) => (
            <Bar key={m.id} dataKey={m.name} stackId="a" fill={MEMBER_COLORS[i % MEMBER_COLORS.length]} />
        ));

    const avgPerWeek = (data.reduce((sum, w) => sum + w.total, 0) / data.length).toFixed(1);

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontFamily: 'Montserrat', fontSize: '11px', fontWeight: 600, color: '#555' }}>Group by:</span>
                    <select
                        value={groupBy}
                        onChange={(e) => setGroupBy(e.target.value)}
                        style={{ padding: '4px 8px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '11px', fontFamily: 'Montserrat' }}
                    >
                        <option value="sessionType">Session Type</option>
                        <option value="member">Team Member</option>
                    </select>
                </div>
                <div style={{ fontFamily: 'Montserrat', fontSize: '11px', color: '#666', backgroundColor: '#f0f7fa', padding: '3px 8px', borderRadius: '12px' }}>
                    Team avg: <strong>{avgPerWeek}</strong> / week
                </div>
            </div>

            <ResponsiveContainer width="100%" height={250}>
                <BarChart data={data} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                    <XAxis dataKey="week" tick={{ fontSize: 10, fontFamily: 'Montserrat', fill: '#888' }} axisLine={{ stroke: '#e0e0e0' }} tickLine={false} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 10, fontFamily: 'Montserrat', fill: '#888' }} axisLine={false} tickLine={false} />
                    <Tooltip contentStyle={{ fontFamily: 'Montserrat', fontSize: '11px', borderRadius: '6px', border: '1px solid #e0e0e0' }} labelStyle={{ fontWeight: 600, marginBottom: '4px' }} />
                    <Legend wrapperStyle={{ fontFamily: 'Montserrat', fontSize: '10px' }} iconType="circle" iconSize={8} />
                    {bars}
                </BarChart>
            </ResponsiveContainer>
        </div>
    );
};

export default TeamStackedBarChart;
